import React, { useCallback, useEffect, useState } from "react";
import { DeleteOutlined } from "@ant-design/icons";
import {
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  message,
} from "antd";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

import { ApiManager, Locale, Request } from "../../utils";
import { getUserInfo } from "../../utils/redux/reducers/User";
import TopupInput from "../balance/TopupInput";
import { useHistory } from "react-router-dom";
import Base from "../Base";

const api = ApiManager.getInstance();
const base = Base.getInstance();
const i18n = Locale.getInstance();

/** Query keys for GET …/merchant/member — Laravel-style filters & pagination:
 *  page, count — pagination;
 *  filter[mobile], filter[keyword], filter[member_no], filter[member_card_id],
 *  filter[created_at_start], filter[created_at_end] (YYYY-MM-DD).
 */
function buildMemberListQuery(page, pageSize, filters) {
  const q = { page, count: pageSize };
  if (filters.mobile) q["filter[mobile]"] = filters.mobile;
  if (filters.keyword) q["filter[keyword]"] = filters.keyword;
  if (filters.member_no) q["filter[member_no]"] = filters.member_no;
  if (
    filters.member_card_id != null &&
    filters.member_card_id !== ""
  ) {
    q["filter[member_card_id]"] = filters.member_card_id;
  }
  if (filters.created_at_start) {
    q["filter[created_at_start]"] = filters.created_at_start;
  }
  if (filters.created_at_end) {
    q["filter[created_at_end]"] = filters.created_at_end;
  }
  return q;
}

function MembersList() {
  const [form] = Form.useForm();
  const [listPayload, setListPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [appliedFilters, setAppliedFilters] = useState({});

  const user = useSelector((state) => getUserInfo(state));
  const merchant = useSelector((state) => state.merchant);
  const history = useHistory();

  useEffect(() => {
    if (!user?.token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await Request.get(
          "/member/api/v6/merchant/member-card",
          user.token
        );
        if (cancelled) return;
        if (res.result && res.data?.data) {
          setCards(res.data.data);
        }
      } catch (e) {
        if (!cancelled) message.error(e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.token]);

  const runFetch = useCallback(async () => {
    if (!user?.token) return;
    setLoading(true);
    try {
      const query = buildMemberListQuery(page, pageSize, appliedFilters);
      const res = await api.member.list.run({
        token: user.token,
        query,
      });
      if (res.result) {
        setListPayload(res.data);
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      base.handleApiError(api.member.list, error);
    } finally {
      setLoading(false);
    }
  }, [user?.token, page, pageSize, appliedFilters]);

  useEffect(() => {
    runFetch();
  }, [runFetch]);

  const onFilterSubmit = () => {
    const values = form.getFieldsValue();
    setAppliedFilters({
      mobile: values.mobile?.trim() || "",
      keyword: values.keyword?.trim() || "",
      member_no: values.member_no?.trim() || "",
      member_card_id: values.member_card_id,
      created_at_start:
        values.created_at?.[0]?.format("YYYY-MM-DD") ?? "",
      created_at_end:
        values.created_at?.[1]?.format("YYYY-MM-DD") ?? "",
    });
    setPage(1);
  };

  const onFilterReset = () => {
    form.resetFields();
    setAppliedFilters({});
    setPage(1);
  };

  const onRemoveMember = (record) => {
    if (!record?.id || !user?.token) return;
    base.showModal(
      i18n.t("member.removeConfirm"),
      Base.Modal.confirm,
      async () => {
        try {
          const res = await api.member.delete.run({
            token: user.token,
            path: { id: record.id },
          });
          if (res.result) {
            message.success(res.message || i18n.t("member.removed"));
            runFetch();
          } else {
            base.showMessage(res.message, Base.Message.warning);
          }
        } catch (error) {
          base.handleApiError(api.member.delete, error);
        }
      }
    );
  };

  const columns = [
    {
      key: "info",
      title: i18n.t("member.info"),
      render: (text, record) => {
        if (record.user) {
          const { user: u } = record;
          return (
            <Space direction="horizontal">
              <Avatar src={u.avatar} size="large" />
              <Space direction="vertical">
                {u.nickname}
                {u.mobile}
              </Space>
            </Space>
          );
        }
        return null;
      },
      width: 200,
    },
    {
      key: "number",
      title: i18n.t("member.number"),
      dataIndex: "member_no",
      width: 200,
    },
    {
      key: "card",
      title: i18n.t("main.sideMenus.membersCard"),
      render: (text, record) => (
        <Tag title={i18n.getPropNameByLocale(record.member_card)} />
      ),
      width: 200,
    },
    {
      key: "joined_at",
      title: i18n.t("member.joined_at"),
      dataIndex: "joined_at",
      width: 200,
    },
    {
      key: "expired_at",
      title: i18n.t("member.expired_at"),
      dataIndex: "expired_at",
      width: 200,
    },
    {
      key: "saved",
      title: i18n.t("member.saved"),
      render: (text, record) => (
        <Statistic
          value={record.total_saved / 100}
          prefix="¥"
          precision={2}
          valueStyle={{ color: "royalblue" }}
        />
      ),
      width: 200,
    },
    {
      key: "actions",
      title: i18n.t("product.cats.actions"),
      align: "center",
      render: (text, record) => (
        <div className="card-item-actions">
          <Button
            type="link"
            onClick={() => {
              history.push({
                pathname: "/main/members/edit",
                state: {
                  memberData: record,
                },
              });
            }}
          >
            {i18n.t("product.productItem.actionEdit")}
          </Button>
          <Divider type="vertical" />
          <Button
            type="link"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => onRemoveMember(record)}
          >
            {i18n.t("member.remove")}
          </Button>
          <Divider type="vertical" />
          <TopupInput
            userId={record.user_id}
            fetchList={() => runFetch()}
            isUserList={merchant?.enable_membership}
          />
        </div>
      ),
      width: 280,
    },
  ];

  const tableRows = listPayload?.data ?? [];
  const total =
    listPayload?.total != null ? listPayload.total : tableRows.length;
  const currentPage = listPayload?.current_page ?? page;

  return (
    <>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical">
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="mobile" label={i18n.t("member.filterMobile")}>
                <Input allowClear placeholder={i18n.t("member.filterMobileHint")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="keyword" label={i18n.t("member.filterKeyword")}>
                <Input allowClear placeholder={i18n.t("member.filterKeywordHint")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="member_no" label={i18n.t("member.filterMemberNo")}>
                <Input allowClear placeholder={i18n.t("member.filterMemberNoHint")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="member_card_id"
                label={i18n.t("member.filterMemberCard")}
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder={i18n.t("member.filterMemberCardPlaceholder")}
                  options={cards.map((c) => ({
                    value: c.id,
                    label: i18n.getPropNameByLocale(c),
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="created_at"
                label={i18n.t("member.filterCreatedAtRange")}
              >
                <DatePicker.RangePicker
                  style={{ width: "100%" }}
                  allowClear
                  presets={[
                    {
                      label: i18n.t("member.filterLast7Days"),
                      value: [dayjs().subtract(6, "day"), dayjs()],
                    },
                    {
                      label: i18n.t("member.filterLast30Days"),
                      value: [dayjs().subtract(29, "day"), dayjs()],
                    },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Space wrap>
            <Button type="primary" onClick={onFilterSubmit}>
              {i18n.t("member.searchFilters")}
            </Button>
            <Button onClick={onFilterReset}>
              {i18n.t("member.resetFilters")}
            </Button>
          </Space>
        </Form>
      </Card>

      <Button
        style={{ marginBottom: 20 }}
        type="primary"
        onClick={() => {
          history.push({ pathname: "/main/members/edit" });
        }}
      >
        {i18n.t("member.add")}
      </Button>
      <Table
        loading={loading}
        dataSource={tableRows}
        columns={columns}
        rowKey={(record) => `member-${record.user_id}`}
        scroll={{
          x: 1400,
        }}
        pagination={{
          current: currentPage,
          pageSize: listPayload?.per_page ?? pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["10", "15", "20", "50"],
          showQuickJumper: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </>
  );
}

export default MembersList;
